import {useCallback, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';

import {DEFAULT_HOMEPAGE_CARD_LAYOUTS, HOMEPAGE_CARD_DEFINITIONS} from '../HomepageCardDefinitions';
import {HomepageCardLayouts, getHomepageCardLayoutCardIds} from './HomepageCardLayout';
import {getSavedHomepageCardLayouts, saveHomepageCardLayouts} from './HomepageCardLayoutPreferences';

export interface HomepageLayoutState {
  currentLayouts: HomepageCardLayouts;
  editingLayout: boolean;
  isSavingLayout: boolean;
  handleDiscardLayout: () => void;
  handleEditLayout: () => void;
  handleLayoutsChange: (layouts: HomepageCardLayouts) => void;
  handleSaveLayout: () => Promise<void>;
}

export default function useHomepageLayout(): HomepageLayoutState {
  const [editingLayout, setEditingLayout] = useState<boolean>(false);
  const [savedLayouts, setSavedLayouts] = useState<HomepageCardLayouts>(() =>
    getSavedHomepageCardLayouts(HOMEPAGE_CARD_DEFINITIONS, DEFAULT_HOMEPAGE_CARD_LAYOUTS)
  );
  const [draftLayouts, setDraftLayouts] = useState<HomepageCardLayouts>(savedLayouts);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const currentLayouts = editingLayout ? draftLayouts : savedLayouts;

  const handleEditLayout = useCallback((): void => {
    setDraftLayouts(savedLayouts);
    setEditingLayout(true);
  }, [savedLayouts]);

  const handleDiscardLayout = useCallback((): void => {
    setDraftLayouts(savedLayouts);
    setEditingLayout(false);
  }, [savedLayouts]);

  const handleLayoutsChange = useCallback((layouts: HomepageCardLayouts): void => {
    setDraftLayouts(layouts);
  }, []);

  const handleSaveLayout = useCallback(async (): Promise<void> => {
    setIsSavingLayout(true);
    try {
      await saveHomepageCardLayouts(draftLayouts, HOMEPAGE_CARD_DEFINITIONS, DEFAULT_HOMEPAGE_CARD_LAYOUTS);
      const cardIdsByBreakpoint = getHomepageCardLayoutCardIds(draftLayouts);
      trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.SAVED_CARD_LAYOUT, {
        narrow_card_ids: cardIdsByBreakpoint.narrow,
        wide_card_ids: cardIdsByBreakpoint.wide,
      });
      setSavedLayouts(draftLayouts);
      setEditingLayout(false);
    } finally {
      setIsSavingLayout(false);
    }
  }, [draftLayouts]);

  return {
    currentLayouts,
    editingLayout,
    isSavingLayout,
    handleDiscardLayout,
    handleEditLayout,
    handleLayoutsChange,
    handleSaveLayout,
  };
}
